import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { useSendToSamboKun } from "@/features/partner/hooks/useSendToSamboKun";

interface SendToSamboKunDialogProps {
  open: boolean;
  onClose: () => void;
  target: "owner" | "allMatters";
  /** バックエンドの解析リクエスト ID（`owner` or `allMatters`） */
  analyzeRequestId: number;
  /**
   * 連携時に除外する picture_request_detail.id の配列。
   * 指定しない場合は解析リクエストに紐づく全物件を連携する。
   */
  excludedPictureRequestDetailIds?: number[];
}

/**
 * 参謀くんへの連携確認ダイアログ。
 *
 * OK を押すと {@link useSendToSamboKun} 経由でバックエンドへ送信する。
 * 成功時の挙動:
 *  - 有効な https: URL が 1 件: 参謀くん側へ自動リダイレクト
 *  - 有効な https: URL が 2 件以上: 自動リダイレクトせず、ダイアログ内に URL リストを表示してユーザーに選択・記録してもらう
 *  - URL がすべて null: リダイレクトせずダイアログを閉じて元画面へ戻る（hook 内で成功 toast 発火）
 *
 * バックドロップクリック・Esc キーによる閉じ操作は送信中の意図しないクローズを防ぐため無効化している。
 *
 * @param props.target - 連携対象（"owner" = 所有者事項解析 / "allMatters" = 全部事項解析）
 * @param props.analyzeRequestId - バックエンド側の解析リクエストID
 */
const SendToSamboKunDialog: React.FC<SendToSamboKunDialogProps> = (props) => {
  const {
    open,
    onClose,
    target,
    analyzeRequestId,
    excludedPictureRequestDetailIds,
  } = props;
  const { send } = useSendToSamboKun();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 複数 redirect URL が返ってきた場合に表示するリスト。空配列の間は確認 UI を出す。
  const [multipleRedirectUris, setMultipleRedirectUris] = useState<string[]>(
    []
  );

  // ダイアログが再オープンされた際に前回の URL リストが残らないようリセット
  useEffect(() => {
    if (open) {
      setMultipleRedirectUris([]);
    }
  }, [open]);

  const handleClose = (): void => {
    onClose();
  };

  const isShowingResult = multipleRedirectUris.length > 0;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      aria-labelledby="send-to-sambo-kun-dialog-title"
      aria-describedby="send-to-sambo-kun-dialog-description"
      fullWidth={isShowingResult}
      maxWidth={isShowingResult ? "sm" : undefined}
    >
      {isShowingResult ? (
        <>
          <DialogTitle id="send-to-sambo-kun-dialog-title">
            連携先を選択してください
          </DialogTitle>
          <DialogContent>
            <Typography
              id="send-to-sambo-kun-dialog-description"
              sx={{ mb: 1 }}
            >
              参謀くんへの連携を受け付けました。複数の連携先が返却されたため、以下から開きたいリンクをクリックしてください。
              <br />
              URL はテキスト選択してコピー（記録）することもできます。
            </Typography>
            <List dense>
              {multipleRedirectUris.map((uri) => (
                <ListItem key={uri} disableGutters>
                  <Link
                    href={uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ wordBreak: "break-all" }}
                  >
                    {uri}
                  </Link>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
            <Button
              label="閉じる"
              onClick={handleClose}
              variant={ButtonVariantOption.Contained}
            />
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle id="send-to-sambo-kun-dialog-title">
            参謀くんへ連携
          </DialogTitle>
          <DialogContent>
            <Typography id="send-to-sambo-kun-dialog-description">
              参謀くんへ連携します。よろしいですか？
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
            <Button
              label="キャンセル"
              onClick={handleClose}
              variant={ButtonVariantOption.Outlined}
              disabled={isSubmitting}
            />
            <SubmitButton
              label="OK"
              timeout={123_000} // apiClient.timeout（120秒）より 3 秒長く設定し、API タイムアウト後のエラー応答をユーザーに表示できるようにする
              disabled={isSubmitting}
              onClick={(_event, cancelLoading) => {
                setIsSubmitting(true);
                const params =
                  target === "owner"
                    ? {
                        target: "owner" as const,
                        ownerAnalyzeRequestId: analyzeRequestId,
                        excludedPictureRequestDetailIds,
                      }
                    : {
                        target: "allMatters" as const,
                        allMattersAnalyzeRequestId: analyzeRequestId,
                        excludedPictureRequestDetailIds,
                      };
                send(params)
                  .then((result) => {
                    if (result.multipleRedirectUris.length > 0) {
                      // 複数 URL の場合は閉じずに結果表示モードへ遷移
                      setMultipleRedirectUris(result.multipleRedirectUris);
                    } else {
                      handleClose();
                    }
                  })
                  .catch(() => {
                    cancelLoading();
                  })
                  .finally(() => {
                    setIsSubmitting(false);
                  });
              }}
              variant={ButtonVariantOption.Contained}
            />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export { SendToSamboKunDialog };
